// De wachtrij die de zinnen van de interviewer op volgorde laat klinken, en
// meteen zwijgt zodra de respondent begint te praten.
//
// De server stuurt de zinnen los terug, genummerd met `seq`. Ze komen niet
// noodzakelijk op volgorde binnen — een korte zin is eerder voorgelezen dan een
// lange — dus de rij wacht op het nummer dat aan de beurt is in plaats van af
// te spelen wat er ligt.
//
// `turnId` is het tweede nummer dat telt. Praat de respondent door de
// interviewer heen, dan zijn de zinnen die daarna nog binnendruppelen van een
// beurt die niemand meer wil horen. Zonder dat nummer praat de app zichzelf na.
//
// AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md

/**
 * @typedef {object} AudioChunk
 * @property {number} seq - Volgnummer binnen de beurt, vanaf 0
 * @property {string} mime - Bijvoorbeeld 'audio/wav'
 * @property {string} base64 - De audiobytes
 */

/**
 * @typedef {object} AudioPlayer
 * @property {(chunk: AudioChunk) => Promise<void>} speel - Speelt één fragment en belooft het einde
 * @property {() => void} stop - Breekt af wat er nu klinkt
 */

/**
 * De echte speler: een <audio> op een blob-url.
 *
 * @returns {AudioPlayer}
 */
const createElementPlayer = () => {
    let audio = null;
    let url = null;
    // De belofte van de zin die nu klinkt. stop() moet hem afronden: een
    // gepauzeerde <audio> vuurt nooit meer `ended`, en dan wacht de wachtrij
    // eeuwig en blijft elke volgende zin van Decca stil.
    let klaar = null;

    const ruimOp = () => {
        if (url) URL.revokeObjectURL(url);
        url = null;
        audio = null;
        klaar = null;
    };

    return {
        speel: (chunk) =>
            new Promise((resolve, reject) => {
                const bytes = Uint8Array.from(atob(chunk.base64), (c) => c.charCodeAt(0));
                url = URL.createObjectURL(new Blob([bytes], { type: chunk.mime }));
                audio = new Audio(url);
                klaar = resolve;
                audio.onended = () => { ruimOp(); resolve(); };
                audio.onerror = () => { ruimOp(); reject(new Error('afspelen mislukt')); };
                audio.play().catch(reject);
            }),
        stop: () => {
            const afronden = klaar;
            if (audio) {
                audio.pause();
                audio.onended = null;
                audio.onerror = null;
            }
            ruimOp();
            afronden?.();
        },
    };
};

/**
 * @typedef {object} AudioQueue
 * @property {(turnId: number, chunk: AudioChunk) => void} push - Zet een fragment in de rij
 * @property {() => void} interrupt - Zwijg nu, en vergeet wat er nog van deze beurt komt
 * @property {() => boolean} isSpeaking - Klinkt er op dit moment iets
 * @property {() => void} reset - Alles leeg, zonder iets af te breken
 */

/**
 * Maakt een wachtrij die zinnen op volgorde afspeelt.
 *
 * @param {object} [options]
 * @param {AudioPlayer} [options.player] - Vervangbaar voor tests
 * @returns {AudioQueue}
 */
const createAudioQueue = ({ player = createElementPlayer() } = {}) => {
    /** @type {Map<number, AudioChunk>} */
    let wachtend = new Map();
    let volgende = 0;
    let huidigeTurn = null;
    // Beurten die onderbroken zijn; alles wat daarvan nog binnenkomt is oud nieuws.
    const afgebroken = new Set();
    let bezig = false;

    const werkAf = async () => {
        if (bezig) return;
        bezig = true;
        try {
            while (wachtend.has(volgende)) {
                const chunk = wachtend.get(volgende);
                wachtend.delete(volgende);
                volgende += 1;
                try {
                    await player.speel(chunk);
                } catch (_error) {
                    // Eén kapot fragment mag het interview niet laten hangen;
                    // de tekst staat al op het scherm.
                }
            }
        } finally {
            bezig = false;
        }
    };

    return {
        push: (turnId, chunk) => {
            if (afgebroken.has(turnId)) return;
            if (turnId !== huidigeTurn) {
                huidigeTurn = turnId;
                wachtend = new Map();
                volgende = 0;
            }
            wachtend.set(chunk.seq, chunk);
            werkAf();
        },

        interrupt: () => {
            if (huidigeTurn !== null) afgebroken.add(huidigeTurn);
            wachtend = new Map();
            player.stop();
        },

        isSpeaking: () => bezig,

        reset: () => {
            wachtend = new Map();
            volgende = 0;
            huidigeTurn = null;
            afgebroken.clear();
        },
    };
};

export { createElementPlayer };
export default createAudioQueue;
